import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { marche as api } from '../services/api';
import { nb, pct1, moisCourt, usePanneau } from './ventesUtils';
import { sequentielle, divergente, Echelle } from './heatmap';
import { PanneauMarche, PERIODES, VPVU, capDep, dateArrete } from './Marche';
import './Ventes.css';
import './Marche.css';

// ─── Marché — Analyse croisée (2026-09-30) ───────────────────────────────────
// Même lecture que les autres analyses croisées (VN, APV, Fournisseurs) :
// une dimension en lignes, les mois en colonnes, une mesure choisie. Le serveur
// renvoie pour chaque ligne le volume N et N-1 de chaque mois, et le marché du
// périmètre (départements et VP/VUL retenus) ; parts et évolutions se calculent
// ici, sans autre appel. Nos lignes (groupe AUBERY) sont en gras.
// Dépliage : marque ▸ modèles, groupe ▸ marques. Cliquer un département le
// retient dans les filtres.

const MESURES = [
  { code: 'part', label: 'Part de marché' },
  { code: 'vol', label: 'Volume' },
  { code: 'evv', label: 'Évol. volume vs N-1' },
  { code: 'evp', label: 'Évol. part vs N-1' },
];
const DIMS = [
  { code: 'marque', label: 'Marque' }, { code: 'modele', label: 'Modèle' }, { code: 'groupe', label: 'Groupe' },
  { code: 'client', label: 'Type de client' }, { code: 'origine', label: 'Origine' }, { code: 'constructeur', label: 'Constructeur' },
  { code: 'departement', label: 'Département' },
];
const DIVERGENTES = ['evv', 'evp'];
// Périodes propres à l'analyse croisée (2026-10-22) : « Depuis 2019 » met une
// colonne par année, chacune coupée aux mêmes mois que l'année en cours (les
// mois précis cochés s'y appliquent : septembre = chaque septembre).
const PERIODES_CROISEE = [...PERIODES, { code: 'annees', label: 'Depuis 2019' }];
const somme = (a) => a.reduce((x, y) => x + y, 0);
const signe = (v, suffixe) => (v == null ? '—' : `${v > 0 ? '+' : ''}${pct1(v).replace(' %', suffixe)}`);

// Valeur d'une cellule (mois i) ou du total d'une ligne (i = null) : les parts
// et les évolutions se RECALCULENT depuis les volumes cumulés, jamais ne se
// somment ni ne se moyennent.
function valeur(o, m, marche, i) {
  const v = i == null ? somme(o.vol) : o.vol[i];
  const v1 = i == null ? somme(o.vol_n1) : o.vol_n1[i];
  const t = i == null ? somme(marche.vol) : marche.vol[i];
  const t1 = i == null ? somme(marche.vol_n1) : marche.vol_n1[i];
  if (m === 'vol') return v;
  if (m === 'part') return t ? (v / t) * 100 : null;
  if (m === 'evv') return v1 ? ((v - v1) / v1) * 100 : null;
  const p = t ? (v / t) * 100 : null; const p1 = t1 ? (v1 / t1) * 100 : null;
  return p != null && p1 != null ? p - p1 : null;
}
const fmt = (v, m) => (v == null ? '—' : m === 'vol' ? (v ? nb(v) : '·') : m === 'part' ? pct1(v) : m === 'evv' ? signe(v, ' %') : signe(v, ' pt'));

export default function MarcheAnalyse() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  const [ouvert, toggle] = usePanneau();
  const [mesure, setMesure] = useState('part');
  const [dimension, setDimension] = useState('marque');
  const [drill, setDrill] = useState({});
  const [open, setOpen] = useState(null);

  const periode = PERIODES_CROISEE.some((x) => x.code === params.get('periode')) ? params.get('periode') : 'ytd';
  const annees = periode === 'annees';
  const moisStr = params.get('mois') || '';
  const moisActifs = moisStr ? moisStr.split(',').filter(Boolean) : [];
  const vpvu = params.get('vpvu') || '';
  const depts = (params.get('depts') || '').split(',').filter(Boolean);
  const clients = (params.get('clients') || '').split('|').filter(Boolean);
  const setFiltre = (cles) => { const next = new URLSearchParams(params); Object.entries(cles).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k))); setParams(next, { replace: true }); };
  const toggleDept = (d) => { const st = new Set(depts); st.has(d) ? st.delete(d) : st.add(d); setFiltre({ depts: [...st].join(',') }); };
  const toggleMois = (c) => { const st = new Set(moisActifs); st.has(c) ? st.delete(c) : st.add(c); setFiltre({ mois: [...st].sort().join(',') }); };
  const toggleClient = (c) => { const st = new Set(clients); st.has(c) ? st.delete(c) : st.add(c); setFiltre({ clients: [...st].join('|') }); };
  const nbActifs = ((moisActifs.length || periode !== 'ytd') ? 1 : 0) + (vpvu ? 1 : 0) + (depts.length ? 1 : 0) + (clients.length ? 1 : 0);
  const requete = { periode, mois: moisStr, vpvu, depts: depts.join(','), clients: clients.join('|') };

  useEffect(() => {
    let actif = true; setErreur(''); setOpen(null); setDrill({});
    api.croisee({ ...requete, dimension })
      .then((r) => { if (actif) setData(r.data); })
      .catch((e) => { if (actif) setErreur(e.response?.data?.error || 'Erreur de chargement'); });
    return () => { actif = false; };
  }, [periode, moisStr, vpvu, params.get('depts'), params.get('clients'), dimension]);   // eslint-disable-line react-hooks/exhaustive-deps

  const ouvrirDrill = async (nom) => {
    if (open === nom) { setOpen(null); return; }
    setOpen(nom);
    if (!drill[nom]) {
      setDrill((d) => ({ ...d, [nom]: 'loading' }));
      try { const r = await api.croisee({ ...requete, dimension, drill: nom }); setDrill((d) => ({ ...d, [nom]: r.data.rows || [] })); }
      catch (e) { setDrill((d) => ({ ...d, [nom]: [] })); }
    }
  };

  const Panneau = (
    <PanneauMarche ouvert={ouvert} onToggle={toggle} periode={moisActifs.length && !annees ? '' : periode} periodes={PERIODES_CROISEE}
      onPeriode={(p) => setFiltre(p === 'annees' ? { periode: p } : { periode: p === 'ytd' ? '' : p, mois: '' })}
      moisActifs={moisActifs} onToggleMois={toggleMois} depts={depts}
      deptsDispo={data?.departementsDispo || ['GUADELOUPE', 'MARTINIQUE', 'GUYANE', 'REUNION', 'MAYOTTE']}
      onToggleDept={toggleDept} clients={clients} clientsDispo={data?.clientsDispo || []} onToggleClient={toggleClient}
      nbActifs={nbActifs} onReset={() => setFiltre({ periode: '', mois: '', vpvu: '', depts: '', clients: '' })} />
  );
  if (erreur) return <><main className="vt-page"><div className="bi-empty"><h2>Rapport indisponible</h2><p>{erreur}</p></div></main>{Panneau}</>;
  if (!data) return <><main className="vt-page"><div className="vt-skel" /></main>{Panneau}</>;

  const { mois, rows, marche } = data;
  const dimObj = DIMS.find((d) => d.code === dimension) || DIMS[0];
  const mes = mesure;
  // Échelles (CLAUDE.md §5) : séquentielle pour le volume et la part (pas de
  // point neutre), divergente autour de 0 pour les évolutions.
  const vals = []; rows.forEach((r) => mois.forEach((_, i) => { const v = valeur(r, mes, marche, i); if (v != null) vals.push(v); }));
  const st = { min: Math.min(...vals, 0), max: Math.max(...vals, 1), mid: 0 };
  const couleur = (v) => (DIVERGENTES.includes(mes) ? divergente(v, { ...st, min: Math.max(st.min, -50), max: Math.min(st.max, 50) }) : sequentielle(v, st.max));
  const nomLigne = (o) => (dimension === 'departement' ? capDep(o.nom) : o.nom);
  const filtrable = dimension === 'departement';
  const totalMarche = { vol: marche.vol, vol_n1: marche.vol_n1 };
  // Colonne de droite en mode années : cumul pour le volume et la part ; pour
  // les évolutions, la dernière année contre la première (un cumul de
  // 2019-2026 contre 2018-2025 ne voudrait rien dire).
  const evolTotale = data.annees && DIVERGENTES.includes(mes);
  const valeurTotale = (o) => (evolTotale
    ? valeur({ vol: [o.vol[o.vol.length - 1]], vol_n1: [o.vol[0]] }, mes,
      { vol: [marche.vol[marche.vol.length - 1]], vol_n1: [marche.vol[0]] }, 0)
    : valeur(o, mes, marche, null));
  const enteteTotale = evolTotale ? `${mois[mois.length - 1]} vs ${mois[0]}` : mes === 'vol' ? 'Total' : 'Période';
  const enteteCol = (m) => (data.annees ? m : moisCourt(m));

  const Cellule = ({ o, i }) => {
    const v = valeur(o, mes, marche, i); const c = couleur(v);
    return <td style={{ background: c.bg }}><span className={`cell${c.mut ? ' mut' : ''}`} style={c.fg ? { color: c.fg } : undefined}>{fmt(v, mes)}</span></td>;
  };
  const Ligne = ({ o }) => (
    <>
      {mois.map((_, i) => <Cellule key={i} o={o} i={i} />)}
      <td className="tot">{fmt(valeurTotale(o), mes)}</td>
    </>
  );

  return (
    <>
      <main className="vt-page">
        <div className="vt-head">
          <div><h1>Marché — Analyse croisée</h1></div>
          <div className="vt-asof"><b>{data.periode}</b>{data.arrete && <><br /><span className={data.arrete.partiel ? 'mk-arrete partiel' : 'mk-arrete'}>données au {dateArrete(data.arrete.date)}{data.arrete.partiel ? ` · ${data.arrete.joursEcoules}/${data.arrete.joursMois} jours ouvrés, ${data.annees ? 'années précédentes' : 'N-1'} sur le mois complet` : ''}</span></>}<br />{depts.length ? depts.map(capDep).join(', ') : 'tous départements'}{vpvu ? ` · ${vpvu}` : ' · VP + VUL'}{clients.length ? ` · ${clients.length === 1 ? clients[0] : `${clients.length} types de client`}` : ''}</div>
        </div>

        <div className="vt-ctl">
          <span><span className="lab">Mesure</span>
            <span className="vt-seg">{MESURES.map((m) => <button key={m.code} className={mesure === m.code ? 'on' : ''} onClick={() => setMesure(m.code)}>{m.label}</button>)}</span>
          </span>
          <span><span className="lab">Dimension</span>
            <span className="vt-seg">{DIMS.map((d) => <button key={d.code} className={dimension === d.code ? 'on' : ''} onClick={() => setDimension(d.code)}>{d.label}</button>)}</span>
          </span>
          <span><span className="lab">Catégorie</span>
            <span className="vt-seg">{VPVU.map((v) => <button key={v.code} className={vpvu === v.code ? 'on' : ''} onClick={() => setFiltre({ vpvu: v.code })}>{v.label}</button>)}</span>
          </span>
        </div>

        <div className="vt-card">
          <div className="vt-card-h"><h2>{MESURES.find((m) => m.code === mes).label} — par {dimObj.label.toLowerCase()} et par {data.annees ? 'année' : 'mois'}</h2>
            <div className="st-tools"><span className="hint">
              part = volume ÷ marché du périmètre {data.annees ? 'de la même année' : 'du même mois'} · « nous » = notre groupe ({data.groupe})
            </span></div>
          </div>
          <div className="vt-card-b vt-tbl-wrap vt-borne">
            <table className="vt-mx tbl-dense">
              <thead><tr><th className="l">{dimObj.label}</th>{mois.map((m) => <th key={m}>{enteteCol(m)}</th>)}<th className="tot">{enteteTotale}</th></tr></thead>
              <tbody>
                {rows.map((o) => {
                  const isOpen = open === o.nom; const sub = drill[o.nom];
                  return (
                    <React.Fragment key={o.nom}>
                      <tr className={`${o.nous ? 'tbl-courant' : ''}${filtrable ? ` tbl-clic${depts.length === 1 && depts[0] === o.nom ? ' actif' : ''}` : ''}`}
                        onClick={filtrable ? () => setFiltre({ depts: depts.length === 1 && depts[0] === o.nom ? '' : o.nom }) : undefined}>
                        <td className={`l ${data.drillable ? '' : 'nodrill'} ${isOpen ? 'open' : ''}`} title={nomLigne(o)}
                          onClick={data.drillable ? () => ouvrirDrill(o.nom) : undefined}>
                          <span className="chev">{data.drillable ? '▶' : ''}</span>{nomLigne(o)}
                          {o.nous && <span className="mk-nous" title={`Ligne de notre groupe (${data.groupe})`}>nous</span>}
                        </td>
                        <Ligne o={o} />
                      </tr>
                      {isOpen && sub === 'loading' && <tr className="sub"><td className="l"><span className="chev" />Chargement…</td><td colSpan={mois.length + 1} /></tr>}
                      {isOpen && Array.isArray(sub) && sub.map((s) => (
                        <tr className={`sub${s.nous ? ' tbl-courant' : ''}`} key={s.nom}>
                          <td className="l" title={s.nom}><span className="chev" />{s.nom}{s.nous && <span className="mk-nous">nous</span>}</td>
                          <Ligne o={s} />
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot><tr>
                <td className="l">Marché du périmètre</td>
                {mois.map((_, i) => <td key={i}>{fmt(valeur(totalMarche, mes, marche, i), mes)}</td>)}
                <td className="tot">{fmt(valeurTotale(totalMarche), mes)}</td>
              </tr></tfoot>
            </table>
            <div className="vt-lgd">
              {DIVERGENTES.includes(mes)
                ? <><span>En recul</span><Echelle divergent /><span>En progression</span></>
                : <><span>Faible</span><Echelle /><span>Élevé</span></>}
            </div>
          </div>
        </div>
      </main>
      {Panneau}
    </>
  );
}
